import type { AttentionItem, AttentionSection } from "components/Document/ReviewInteraction";
import type { Issue } from "generated-graphql/graphql";

import { safeParseInt } from "encamp-shared/src/tierii/utilities";

import type { ReviewFieldDiffIndex } from "./reviewFieldDiffs";

/**
 * Everything on a manifest under review that wants the reviewer's attention, in
 * the order they read the form.
 *
 * The same walk the extraction grid does, over this form's own reasons. The two
 * screens do the same job — an extractor proposed values, a reviewer confirms
 * them — and a reviewer moving between them should not find that one of them
 * points at what needs looking at and the other leaves them to scroll.
 *
 * One walk feeds the count, the list and travel, so they cannot disagree about
 * what is unresolved or what order it is in.
 */

/**
 * Why a control wants attention here.
 *
 * Not the grid's four. A manifest has no record to be written over — it *is* the
 * record — but it has something the grid does not: two other source pillars that
 * may already know the answer.
 */
export type ManifestAttentionKind = "issue" | "sourceDisagreement" | "lowConfidence";

export const MANIFEST_ATTENTION_KINDS: readonly ManifestAttentionKind[] = [
  "issue",
  "sourceDisagreement",
  "lowConfidence",
];

/**
 * How each reason reads as a heading, and the order the reviewer meets them.
 *
 * Validation first: it is the only one saying a value is *wrong* rather than
 * worth a look. Then a disagreement, where another source has a different answer.
 * Then the readings the model itself was unsure of, which are the softest signal.
 */
export const MANIFEST_ATTENTION_SECTIONS: readonly AttentionSection<ManifestAttentionKind>[] = [
  { kind: "issue", title: "Fix before confirming" },
  { kind: "sourceDisagreement", title: "Another source disagrees" },
  { kind: "lowConfidence", title: "The AI was unsure" },
];

/** Only the validator's objections turn the control red; the rest are questions. */
export const MANIFEST_ATTENTION_ERROR_KINDS: readonly ManifestAttentionKind[] = ["issue"];

/** Below this the model's own confidence is worth flagging. */
export const MANIFEST_LOW_CONFIDENCE_THRESHOLD = 0.7;

/** One field's confidence, as the reading query returns it. */
export interface ManifestFieldConfidence {
  field: string;
  wasteLineNumber?: string | null;
  transporterLineNumber?: string | null;
  confidence?: number | null;
  wasEdited: boolean;
}

/** The rows on the form, in rendered order, so a printed number resolves to a path. */
export interface ManifestRows {
  wasteLines: readonly string[];
  transporters: readonly string[];
}

/** The form path for a field, given the row it belongs to. */
function pathFor(
  field: string,
  rows: ManifestRows,
  wasteLineNumber?: string | null,
  transporterLineNumber?: string | null
): string | null {
  if (wasteLineNumber != null) {
    const index = rows.wasteLines.indexOf(wasteLineNumber);
    return index < 0 ? null : `wasteLines.${index}.${field}`;
  }
  if (transporterLineNumber != null) {
    const index = rows.transporters.indexOf(transporterLineNumber);
    return index < 0 ? null : `transporters.${index}.${field}`;
  }
  return field;
}

/** A field's label for the list, from its path. */
function labelFor(path: string): string {
  const row = /^(wasteLines|transporters)\.(\d+)\.(.+)$/.exec(path);
  if (row == null) return path;
  const [, group, indexText, field] = row;
  const index = safeParseInt(indexText);
  if (index == null) return path;
  const noun = group === "wasteLines" ? "line" : "transporter";
  return `${field} (${noun} ${index + 1})`;
}

export function listManifestAttention({
  issues,
  fieldDiffs,
  confidences = [],
  rows,
  acknowledged,
}: {
  /** Validation issues, already keyed to the form's field paths. */
  issues: readonly Issue[];
  fieldDiffs: ReviewFieldDiffIndex;
  confidences?: readonly ManifestFieldConfidence[];
  rows: ManifestRows;
  /** Paths the reviewer has answered by keeping what the form holds. */
  acknowledged?: ReadonlySet<string>;
}): AttentionItem<ManifestAttentionKind>[] {
  const items: AttentionItem<ManifestAttentionKind>[] = [];

  for (const issue of issues) {
    // An issue keyed to nothing on this form still has to be reachable in the
    // list, even though there is nowhere to travel to — silently dropping it
    // would leave a reviewer blocked by something invisible.
    items.push({
      kind: "issue",
      path: issue.key ?? "",
      label: issue.key == null ? issue.message : labelFor(issue.key),
      detail: issue.key == null ? undefined : issue.message,
    });
  }

  for (const [path] of fieldDiffs.manifestFieldDiffs) {
    if (acknowledged?.has(path)) continue;
    items.push({
      kind: "sourceDisagreement",
      path,
      label: labelFor(path),
      detail: "e-Manifest, vendor and the document do not agree",
    });
  }

  for (const [key] of fieldDiffs.wasteLineFieldDiffs) {
    const [wasteLineNumber, field] = key.split(":");
    const path = pathFor(field, rows, wasteLineNumber);
    if (path == null || acknowledged?.has(path)) continue;
    items.push({
      kind: "sourceDisagreement",
      path,
      label: labelFor(path),
      detail: "e-Manifest, vendor and the document do not agree",
    });
  }

  for (const score of confidences) {
    // A field the reviewer already corrected on a previous pass is answered, and
    // the model's doubt about its own superseded reading is not worth raising.
    if (score.wasEdited) continue;
    if (score.confidence == null || score.confidence >= MANIFEST_LOW_CONFIDENCE_THRESHOLD) continue;

    const path = pathFor(score.field, rows, score.wasteLineNumber, score.transporterLineNumber);
    if (path == null || acknowledged?.has(path)) continue;

    items.push({
      kind: "lowConfidence",
      path,
      label: labelFor(path),
      detail: `the AI was ${Math.round(score.confidence * 100)}% sure`,
    });
  }

  return items;
}
